import { ArrowRight, CheckCircle2, Download, FolderSync, HardDriveDownload, Laptop, ListChecks, ShieldCheck } from "lucide-react";
import type { Locale } from "./i18n";
import { localizedPath } from "./route";
import { getGrabSiloDesktopCopy } from "./grabSiloDesktopI18n";
import { getDesktopMacPreviewCopy } from "./desktopMacPreviewI18n";
import { desktopDownloads, getDesktopDownloadCopy } from "./desktopDownloads";
import { desktopTrialPolicy } from "./desktopTrialI18n";
import { getDesktopTrialMarketingCopy } from "./desktopTrialMarketing";
import "./desktopTrial.css";
import { useEffect, useState } from "react";
import { DesktopCheckout } from "./DesktopCheckout";
import { paymentRequest, type DesktopPaymentProduct } from "./desktopPayments";

const checkoutURL = (import.meta.env.VITE_GRABSILO_DESKTOP_CHECKOUT_URL || "").trim();
const macQuarantineCommand = 'xattr -dr com.apple.quarantine "/Applications/SiloSave Desktop.app"';

export function GrabSiloDesktopPage({ locale, macArm64Url = "", macX64Url = "" }: { locale: Locale; macArm64Url?: string; macX64Url?: string }) {
  const copy = getGrabSiloDesktopCopy(locale);
  const mac = getDesktopMacPreviewCopy(locale);
  const downloadCopy = getDesktopDownloadCopy(locale);
  const trialCopy = getDesktopTrialMarketingCopy(locale);
  const [product, setProduct] = useState<DesktopPaymentProduct | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    paymentRequest<DesktopPaymentProduct>("/api/v1/payments/desktop", { signal: abort.signal }).then(setProduct).catch(() => {});
    return () => abort.abort();
  }, []);
  const purchaseHref = product?.enabled ? "#desktop-checkout" : checkoutURL || "mailto:support@silosave.com?subject=SiloSave%20Desktop%20license";
  const purchaseCta = product?.enabled || checkoutURL ? copy.primaryCta : copy.supportCta;

  return (
    <main className="desktop-product-main">
      <section className="desktop-product-hero" aria-labelledby="desktop-product-title">
        <div className="desktop-product-copy">
          <p className="section-kicker">{copy.eyebrow}</p>
          <h1 id="desktop-product-title">{copy.title}</h1>
          <h2 className="desktop-trial-headline">{trialCopy.headline}</h2>
          <p className="desktop-product-lead">{copy.lead}</p>
          <div className="desktop-product-badges">
            {copy.badges.map((badge, index) => <span key={badge}><CheckCircle2 size={15} />{index === 1 ? trialCopy.badge : badge}</span>)}
          </div>
          <div className="desktop-product-actions">
            <a className="desktop-product-primary" href={desktopDownloads.windows} rel="nofollow"><Download size={17} />{trialCopy.cta} · Windows</a>
            <a className="desktop-product-secondary" href={localizedPath(locale, "home")}>{copy.webCta}</a>
          </div>
          <p className="desktop-trial-reassurance">{trialCopy.reassurance}</p>
          <a className="desktop-trial-license-link" href="#license">{trialCopy.license}</a>
          <div className="desktop-product-actions desktop-mac-preview-actions">
            <a className="desktop-product-secondary" href={macArm64Url || desktopDownloads.macArm64} rel="nofollow"><Download size={17} />{mac.arm64[0]} · {downloadCopy.preview}</a>
            <a className="desktop-product-secondary" href={macX64Url || desktopDownloads.macX64} rel="nofollow"><Download size={17} />{mac.x64[0]} · {downloadCopy.preview}</a>
          </div>
        </div>

        <div className="desktop-window" aria-label="SiloSave Desktop application overview">
          <div className="desktop-window-bar"><span /><span /><span /><strong>SiloSave Desktop</strong></div>
          <div className="desktop-window-body">
            <aside>
              <strong><Download size={16} />{copy.previewDownload}</strong>
              <span><FolderSync size={16} />{copy.previewAccounts}</span>
              <span><ListChecks size={16} />{copy.previewTasks}</span>
            </aside>
            <div>
              <small>{copy.previewKicker}</small>
              <h2>{copy.previewHeadline}</h2>
              <div className="desktop-window-input"><span>https://…</span><button type="button" tabIndex={-1}>{copy.previewResolve}</button></div>
              <div className="desktop-window-task"><HardDriveDownload size={18} /><div><strong>{copy.previewQueue}</strong><span>{copy.previewReady}</span></div><em>{copy.previewLocal}</em></div>
            </div>
          </div>
        </div>
      </section>

      <section className="desktop-product-definition" aria-labelledby="desktop-definition-title">
        <p className="section-kicker">{trialCopy.kicker}</p>
        <h2 id="desktop-definition-title">{trialCopy.headline}</h2>
        <p>{desktopTrialPolicy(locale)}</p>
      </section>

      <section className="desktop-product-features" aria-labelledby="desktop-features-title">
        <div><p className="section-kicker">{copy.featuresKicker}</p><h2 id="desktop-features-title">{copy.featuresTitle}</h2></div>
        <div className="desktop-feature-grid">
          {copy.features.map(([title, body], index) => {
            const Icon = [HardDriveDownload, FolderSync, Laptop, ListChecks][index];
            return <article key={title}><Icon size={22} /><h3>{title}</h3><p>{body}</p></article>;
          })}
        </div>
      </section>

      {(macArm64Url || macX64Url) && (
        <section className="desktop-product-mac" id="mac-preview" aria-labelledby="desktop-mac-title">
          <div>
            <p className="section-kicker">{mac.kicker}</p>
            <h2 id="desktop-mac-title">{mac.title}</h2>
            <p>{mac.body}</p>
            <div className="desktop-mac-downloads">
              {macArm64Url && <a href={macArm64Url} rel="nofollow"><Download size={18} /><span><strong>{mac.arm64[0]}</strong><small>{mac.arm64[1]}</small></span></a>}
              {macX64Url && <a href={macX64Url} rel="nofollow"><Download size={18} /><span><strong>{mac.x64[0]}</strong><small>{mac.x64[1]}</small></span></a>}
            </div>
            <p className="desktop-mac-requirement">{mac.requirement}</p>
          </div>
          <div className="desktop-mac-steps">
            <h3>{mac.stepsTitle}</h3>
            <ol>{mac.steps.map((step) => <li key={step}>{step}</li>)}</ol>
            <p>{mac.terminalLabel}</p>
            <code>{macQuarantineCommand}</code>
          </div>
        </section>
      )}

      <section className="desktop-product-separation" id="license" aria-label="Product and license details">
        <article><ShieldCheck size={24} /><p className="section-kicker">{copy.licenseKicker}</p><h2>{copy.licenseTitle}</h2><p>{copy.licenseBody}</p></article>
        <article><Laptop size={24} /><p className="section-kicker">{copy.webKicker}</p><h2>{copy.webTitle}</h2><p>{copy.webBody}</p><a href={localizedPath(locale, "home")}>{copy.webCta}<ArrowRight size={15} /></a></article>
        <article><CheckCircle2 size={24} /><p className="section-kicker">{copy.responsibilityKicker}</p><h2>{copy.responsibilityTitle}</h2><p>{copy.responsibilityBody}</p></article>
      </section>

      <section className="desktop-product-checkout" aria-labelledby="desktop-checkout-title">
        <div><p className="section-kicker">{copy.checkoutKicker}</p><h2 id="desktop-checkout-title">{copy.checkoutTitle}</h2>{!product?.enabled && <p>{copy.checkoutBody}</p>}<DesktopCheckout locale={locale} product={product} /></div>
        {!product?.enabled && <a href={purchaseHref}>{purchaseCta}<ArrowRight size={17} /></a>}
      </section>

      <section className="desktop-product-faq" id="desktop-faq" aria-labelledby="desktop-faq-title">
        <div><p className="section-kicker">{copy.faqKicker}</p><h2 id="desktop-faq-title">{copy.faqTitle}</h2></div>
        <div>{copy.faqs.map(([question, answer]) => <article key={question}><h3>{question}</h3><p>{answer}</p></article>)}</div>
      </section>
    </main>
  );
}
